/**
 * Client-side tools for AI Chat.
 *
 * Each tool lives in its own file and exports a {@link ClientTool}.
 * To add a new client tool:
 *
 * 1. Create a new file in this directory (e.g., `myTool.ts`)
 * 2. Define an input type and build the tool via `createClientTool<T>()`
 * 3. Export the resulting `ClientTool`
 * 4. Import and register it in the `TOOL_REGISTRY` map below
 *
 * See `navigate.ts` for a full example with dependencies and parameterized routing.
 */
import type {
  ClientTool,
  ClientToolConfig,
  ClientToolDeps,
  ClientToolExecutionResult,
} from "./ClientTools";
import { createClientTool } from "./ClientTools";
import { tool as navigateTool } from "./navigate";

export type {
  ClientTool,
  ClientToolConfig,
  ClientToolDeps,
  ClientToolExecutionResult,
};
export { createClientTool };

/**
 * Registry of all client tools, keyed by tool name.
 *
 * Add new tools here after creating their module file.
 */
const TOOL_REGISTRY: ReadonlyMap<string, ClientTool> = new Map([
  [navigateTool.spec.name, navigateTool],
]);

/**
 * All client-side tool specs, sent to the backend with every chat request
 * so the LLM knows these tools are available.
 */
export const BROWSER_CLIENT_TOOLS = [...TOOL_REGISTRY.values()].map(
  (tool) => tool.spec,
);

/**
 * Execute a client-side tool by name with the given input parameters.
 *
 * Looks up the tool in the registry and calls its `execute` function.
 * Returns an error result for unknown tools.
 */
export function executeClientTool(
  toolName: string,
  params: Record<string, unknown>,
  deps?: ClientToolDeps,
): ClientToolExecutionResult {
  const tool = TOOL_REGISTRY.get(toolName);
  if (!tool) {
    return {
      response: { error: `Unknown client tool: ${toolName}` },
      success: false,
    };
  }
  return tool.execute(params, deps);
}
